import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Tag, Button, Space, Drawer, Form, Select, InputNumber, Input,
  DatePicker, message, Modal, Alert, Descriptions, Tooltip
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, PartitionOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  ErpDataGrid, ErpMasterShell, formatINR
} from '../../../../components/common/erp-grid';
import useAuth from '../../../../hooks/useAuth';
import { customerReceiptsApi } from './customerReceiptsApi';
import AllocateAdvanceModal from './AllocateAdvanceModal';
import { customerApi } from '../../masters/customers/customerApi';
import { proformaApi } from '../../sales/proforma/proformaApi';
import {
  RECEIPT_STATUS_TABS, statusForTab, RECEIPT_MODES, FREE_RECEIPT_PURPOSES,
  modeLabel, purposeLabel, receiptStatusLabel, receiptStatusColor,
  canRecordReceipt, canPostReceipt, receiptActionsFor, formatMoney, overLimitMessage,
  onAccountAdvance
} from './customerReceiptsHelpers';
import '../accounts.css';

// ════════════════════════════════════════════════════════════════════
// Stage SALES-RECEIPTS-1 — Customer Receipts (Accounts).
//
// Sales records DRAFT receipts (against a confirmed proforma, or free); Accounts
// posts them to the GL through the existing Receipt Voucher engine (Dr Bank/Cash
// · Cr Customer "Sundry Debtors"). A live customer position (operational
// exposure) flags over-credit-limit and steers to Management Approval.
//
// Chrome mirrors the Payment / Receipt Voucher page: the reusable ErpDataGrid
// inside ErpMasterShell (compact banded rows, sticky header, toolbar search /
// columns / export, Indian-numbered pagination) on the `erp-accounts-page`
// theme. Post / Allocate / Cancel live on the row right-click menu, exactly as
// Post / Cancel do on the Payments grid.
// ════════════════════════════════════════════════════════════════════

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}
function normList(data) {
  if (Array.isArray(data)) return data;
  return (data && Array.isArray(data.rows)) ? data.rows : [];
}
function fmtDate(v) {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('DD MMM YYYY') : '';
}

export default function CustomerReceiptsPage() {
  const { me } = useAuth();
  const permissions = me && me.permissions;
  const mayCreate = canRecordReceipt(permissions);
  const mayPost = canPostReceipt(permissions);

  const [tab, setTab] = useState('all');
  const [customerFilter, setCustomerFilter] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [position, setPosition] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const drawerCustomerId = Form.useWatch('customerId', form);
  const drawerProformaId = Form.useWatch('proformaId', form);
  const [customerProformas, setCustomerProformas] = useState([]);

  const [cancelState, setCancelState] = useState({ open: false, row: null, reason: '', busy: false });
  // Allocate an on-account advance across confirmed PIs (Stage SALES-RECEIPTS-2).
  const [allocState, setAllocState] = useState({ open: false, row: null, receipt: null, proformas: [], saving: false });

  // ── loaders ──
  const loadReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customerReceiptsApi.list({
        status: statusForTab(tab),
        customerId: customerFilter || undefined,
        limit: 200
      });
      setRows(normList(data));
    } catch (err) {
      message.error(backendError(err, 'Could not load receipts.'));
      setRows([]);
    } finally { setLoading(false); }
  }, [tab, customerFilter]);

  const loadCustomers = useCallback(async (q) => {
    try {
      const data = await customerApi.listParentCustomers({ q: q || undefined });
      setCustomers(normList(data));
    } catch (_e) { setCustomers([]); }
  }, []);

  const loadLedgers = useCallback(async () => {
    try { setLedgers(normList(await customerReceiptsApi.cashBankLedgers())); }
    catch (_e) { setLedgers([]); }
  }, []);

  const loadPosition = useCallback(async (customerId) => {
    if (!customerId) { setPosition(null); return; }
    try { setPosition(await customerReceiptsApi.position(customerId)); }
    catch (_e) { setPosition(null); }
  }, []);

  useEffect(() => { loadReceipts(); }, [loadReceipts]);
  useEffect(() => { loadCustomers(''); loadLedgers(); }, [loadCustomers, loadLedgers]);
  useEffect(() => { loadPosition(customerFilter); }, [customerFilter, loadPosition]);

  const refresh = useCallback(() => { loadReceipts(); loadPosition(customerFilter); }, [loadReceipts, loadPosition, customerFilter]);

  // Drawer: when a customer is picked, load their confirmed proformas.
  useEffect(() => {
    let alive = true;
    if (!drawerOpen || !drawerCustomerId) { setCustomerProformas([]); return () => { alive = false; }; }
    (async () => {
      try {
        const data = await proformaApi.list({ customerId: drawerCustomerId, status: 'CONFIRMED', limit: 100 });
        if (alive) setCustomerProformas(normList(data));
      } catch (_e) { if (alive) setCustomerProformas([]); }
    })();
    return () => { alive = false; };
  }, [drawerOpen, drawerCustomerId]);

  const customerOptions = useMemo(
    () => customers.map((c) => ({ value: c.id, label: `${c.customerName || c.customer_name || 'Customer'}${c.customerCode || c.customer_code ? ` · ${c.customerCode || c.customer_code}` : ''}` })),
    [customers]
  );
  const ledgerOptions = useMemo(
    () => ledgers.map((l) => ({ value: l.id, label: `${l.accountName || l.account_name || 'Ledger'}${l.accountCode || l.account_code ? ` · ${l.accountCode || l.account_code}` : ''}` })),
    [ledgers]
  );
  const proformaOptions = useMemo(
    () => customerProformas.map((p) => ({ value: p.id, label: `${p.proformaNo || p.proforma_no || p.id} · ${formatMoney(p.grandTotal ?? p.grand_total)}` })),
    [customerProformas]
  );

  // ── actions ──
  const openDrawer = () => {
    form.resetFields();
    form.setFieldsValue({ receiptDate: dayjs(), mode: 'bank', purpose: 'RECEIPT' });
    setDrawerOpen(true);
  };
  const closeDrawer = () => { setDrawerOpen(false); setCustomerProformas([]); };

  const submitDraft = async () => {
    let values;
    try { values = await form.validateFields(); } catch (_e) { return; }
    setSaving(true);
    try {
      const payload = {
        customerId: values.customerId,
        proformaId: values.proformaId || undefined,
        amount: values.amount,
        mode: values.mode,
        ledgerAccountId: values.ledgerAccountId,
        receiptDate: values.receiptDate ? values.receiptDate.format('YYYY-MM-DD') : undefined,
        bankReference: values.bankReference || undefined,
        remarks: values.remarks || undefined
      };
      // PI-linked receipts are always PI advances; only send purpose for a free receipt.
      if (!values.proformaId) payload.purpose = values.purpose || 'RECEIPT';
      await customerReceiptsApi.create(payload);
      message.success('Draft receipt recorded. Accounts can now post it.');
      closeDrawer();
      refresh();
    } catch (err) {
      message.error(backendError(err, 'Could not record the receipt.'));
    } finally { setSaving(false); }
  };

  const doPost = useCallback(async (row) => {
    setBusyId(row.id);
    try {
      await customerReceiptsApi.post(row.id);
      message.success(`Receipt ${row.paymentNo || ''} posted to the ledger.`);
      refresh();
    } catch (err) {
      message.error(backendError(err, 'Could not post the receipt.'));
    } finally { setBusyId(null); }
  }, [refresh]);

  // Open the allocate modal — fetch the receipt + its confirmed-PI context.
  const openAllocate = useCallback(async (row) => {
    setAllocState({ open: true, row, receipt: null, proformas: [], saving: false });
    try {
      const ctx = await customerReceiptsApi.allocationContext(row.id);
      setAllocState((s) => (s.open && s.row && s.row.id === row.id
        ? { ...s, receipt: ctx.receipt, proformas: ctx.proformas || [] } : s));
    } catch (err) {
      message.error(backendError(err, 'Could not load the allocation.'));
      setAllocState({ open: false, row: null, receipt: null, proformas: [], saving: false });
    }
  }, []);
  const saveAllocate = useCallback(async (allocations) => {
    const row = allocState.row;
    if (!row) return;
    setAllocState((s) => ({ ...s, saving: true }));
    try {
      await customerReceiptsApi.allocate(row.id, allocations);
      message.success('Advance allocated.');
      setAllocState({ open: false, row: null, receipt: null, proformas: [], saving: false });
      refresh();
    } catch (err) {
      message.error(backendError(err, 'Could not allocate the advance.'));
      setAllocState((s) => ({ ...s, saving: false }));
    }
  }, [allocState.row, refresh]);

  const submitCancel = useCallback(async () => {
    const { row, reason } = cancelState;
    if (!reason.trim()) { message.warning('A reason is required to cancel.'); return; }
    setCancelState((s) => ({ ...s, busy: true }));
    try {
      await customerReceiptsApi.cancel(row.id, reason.trim());
      message.success('Receipt cancelled; the ledger entry was reversed.');
      setCancelState({ open: false, row: null, reason: '', busy: false });
      refresh();
    } catch (err) {
      message.error(backendError(err, 'Could not cancel the receipt.'));
      setCancelState((s) => ({ ...s, busy: false }));
    }
  }, [cancelState, refresh]);

  // ── grid columns (ErpDataGrid shape, mirroring the Payments grid) ──
  const columns = useMemo(() => [
    { title: '#', key: '__sno__', width: 52, align: 'center',
      render: (_v, _r, idx) => <span className="erp-grid__muted">{idx + 1}</span> },
    { title: 'Receipt No', dataIndex: 'paymentNo', key: 'paymentNo', width: 160, fixed: 'left',
      sorter: (a, b) => String(a.paymentNo || '').localeCompare(String(b.paymentNo || '')),
      render: (v) => v ? <code className="erp-grid__code">{v}</code> : null },
    { title: 'Date', dataIndex: 'receiptDate', key: 'receiptDate', width: 120, fixed: 'left', dataType: 'date',
      sorter: (a, b) => String(a.receiptDate || '').localeCompare(String(b.receiptDate || '')),
      render: fmtDate },
    { title: 'Customer', dataIndex: 'customerName', key: 'customerName', ellipsis: true,
      render: (v, r) => v ? (
        <span>{r.customerCode ? <code style={{ fontSize: 11, color: '#888', marginRight: 6 }}>{r.customerCode}</code> : null}{v}</span>
      ) : null },
    { title: 'Proforma', dataIndex: 'proformaNo', key: 'proformaNo', width: 150,
      render: (v) => v ? <code className="erp-grid__code">{v}</code> : null },
    { title: 'Purpose', dataIndex: 'purpose', key: 'purpose', width: 130, dataType: 'enum',
      render: (v) => <Tag className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>{purposeLabel(v)}</Tag> },
    { title: 'Amount (₹)', dataIndex: 'amount', key: 'amount', align: 'right', width: 140,
      sorter: (a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0),
      render: (v) => { const n = Number(v) || 0; return n === 0 ? null : formatINR(n); } },
    { title: 'Mode', dataIndex: 'mode', key: 'mode', width: 100, dataType: 'enum',
      render: (v) => <Tag className="erp-grid__tag">{modeLabel(v)}</Tag> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 110, align: 'center', dataType: 'enum',
      groupable: true, groupField: 'status',
      render: (v) => (
        <Tag color={receiptStatusColor(v)} className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>
          {(receiptStatusLabel(v) || '').toUpperCase()}
        </Tag>
      ) },
    // Inline actions — Post / Allocate / Cancel, one click. Post & Cancel derive
    // from receiptActionsFor (post permission + status); Allocate is offered on a
    // POSTED on-account advance. Right-click still exposes the same actions.
    { title: 'Action', key: 'actions', width: 210, align: 'center', emptyFallback: false,
      render: (_v, row) => {
        const a = receiptActionsFor(row, permissions);
        const canAlloc = mayPost && row.status === 'posted' && row.purpose === 'ADVANCE';
        if (!a.post && !a.cancel && !canAlloc) return <span className="erp-grid__muted">—</span>;
        return (
          <Space size={4} onClick={(e) => e.stopPropagation()}>
            {a.post ? <Button size="small" type="primary" icon={<CheckCircleOutlined />} loading={busyId === row.id} onClick={() => doPost(row)}>Post</Button> : null}
            {canAlloc ? <Tooltip title="Allocate this advance across confirmed orders"><Button size="small" icon={<PartitionOutlined />} onClick={() => openAllocate(row)}>Allocate</Button></Tooltip> : null}
            {a.cancel ? <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => setCancelState({ open: true, row, reason: '', busy: false })}>Cancel</Button> : null}
          </Space>
        );
      } }
  ], [permissions, mayPost, busyId, doPost, openAllocate]);

  // Right-click actions — mirror the Payments grid (Post / Cancel live here);
  // Allocate is offered on a POSTED on-account advance (Accounts action).
  const contextMenuActions = useMemo(() => ([
    {
      key: 'post', label: 'Post to ledger', icon: <CheckCircleOutlined />,
      visible: (row) => receiptActionsFor(row, permissions).post,
      disabled: (row) => busyId === row.id,
      onClick: doPost
    },
    {
      key: 'allocate', label: 'Allocate advance', icon: <PartitionOutlined />,
      visible: (row) => mayPost && row.status === 'posted' && row.purpose === 'ADVANCE',
      onClick: openAllocate
    },
    {
      key: 'cancel', label: 'Cancel receipt', icon: <CloseCircleOutlined />, danger: true,
      visible: (row) => receiptActionsFor(row, permissions).cancel,
      onClick: (row) => setCancelState({ open: true, row, reason: '', busy: false })
    }
  ]), [permissions, busyId, mayPost, doPost, openAllocate]);

  const statusOptions = useMemo(
    () => RECEIPT_STATUS_TABS.map((t) => ({ value: t.key, label: t.label })),
    []
  );
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);
  const actionsProp = useMemo(() => ({ add: mayCreate }), [mayCreate]);

  const toolbarExtra = useMemo(() => (
    <Space size="small" wrap>
      <span className="erp-cr-toolbar-label">Customer:</span>
      <Select
        allowClear showSearch filterOption={false}
        size="small" style={{ width: 240 }}
        placeholder="All customers"
        options={customerOptions}
        value={customerFilter}
        onSearch={loadCustomers}
        onChange={(v) => setCustomerFilter(v || null)}
      />
      <span className="erp-cr-toolbar-label">Status:</span>
      <Select
        size="small" style={{ width: 130 }}
        value={tab}
        onChange={setTab}
        options={statusOptions}
      />
    </Space>
  ), [customerOptions, customerFilter, loadCustomers, tab, statusOptions]);

  const overMsg = overLimitMessage(position);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        {/* Selected customer's operational position — grounds the credit call. */}
        {position ? (
          <div className="erp-cr-position">
            <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 3 }}
              items={[
                { key: 'due', label: 'Confirmed dues', children: <span className="tabular-nums">{formatMoney(position.confirmedDues)}</span> },
                { key: 'in', label: 'Received (net)', children: <span className="tabular-nums">{formatMoney(position.netMoneyIn)}</span> },
                {
                  key: 'onacct', label: 'On-account advance',
                  children: <Tooltip title="Advance received not tied to a specific order — reduces total outstanding.">
                    <span className={`tabular-nums ${onAccountAdvance(position) > 0 ? 'text-emerald-600 font-medium' : 'text-slate-400'}`}>{formatMoney(onAccountAdvance(position))}</span>
                  </Tooltip>
                },
                { key: 'out', label: 'Outstanding', children: <span className="tabular-nums font-semibold">{formatMoney(position.outstanding)}</span> },
                {
                  key: 'lim', label: 'Credit limit',
                  children: position.creditLimitSet
                    ? <span className={position.overLimit ? 'text-red-600 font-semibold tabular-nums' : 'tabular-nums'}>{formatMoney(position.creditLimit)}</span>
                    : <span className="text-slate-400">Not set</span>
                }
              ]}
            />
            {overMsg ? <Alert type="warning" showIcon className="mt-2" message={overMsg} /> : null}
          </div>
        ) : null}

        <ErpDataGrid
          title="Customer Receipts"
          gridId="accounts-customer-receipts-v1"
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          rowHeight="compact"
          stickyHeader
          selectable
          pageSize={25}
          pageSizeOptions={pageSizeOptions}
          searchable
          searchPlaceholder="Search by receipt no / customer / proforma / mode…"
          searchShortcut
          exportable
          exportFilename="customer-receipts"
          actions={actionsProp}
          addButtonText="New receipt"
          onAdd={openDrawer}
          onRefresh={refresh}
          contextMenuActions={contextMenuActions}
          toolbarExtra={toolbarExtra}
          emptyTitle="No receipts yet"
          emptyText={mayCreate ? 'Click New receipt to record a customer advance or payment.' : 'No customer receipts to display.'}
          emptyCtaLabel={mayCreate ? 'New receipt' : undefined}
        />
      </div>

      {/* New receipt — Sales records a draft; Accounts posts it later. */}
      <Drawer
        title="New customer receipt"
        open={drawerOpen}
        onClose={closeDrawer}
        width={460}
        destroyOnHidden
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={submitDraft}>Save draft</Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" requiredMark="optional" className="erp-accounts-form">
          <Form.Item name="customerId" label="Customer" rules={[{ required: true, message: 'Pick a customer.' }]}>
            <Select showSearch filterOption={false} placeholder="Select customer" options={customerOptions}
              onSearch={loadCustomers}
              onChange={() => form.setFieldsValue({ proformaId: undefined })} />
          </Form.Item>
          <Form.Item name="proformaId" label="Against proforma (optional)"
            extra="Link a confirmed proforma to record a PI advance. Leave empty for a lump-sum receipt not tied to one order (choose “Advance (on account)” below).">
            <Select allowClear showSearch optionFilterProp="label" placeholder={drawerCustomerId ? 'Select a confirmed proforma' : 'Pick a customer first'}
              disabled={!drawerCustomerId} options={proformaOptions} />
          </Form.Item>
          {!drawerProformaId ? (
            <Form.Item name="purpose" label="Purpose" rules={[{ required: true, message: 'Pick a purpose.' }]}
              extra="“Advance (on account)” = a lump sum held as unapplied credit against all the customer’s orders.">
              <Select options={FREE_RECEIPT_PURPOSES.map((p) => ({ value: p, label: purposeLabel(p) }))} />
            </Form.Item>
          ) : null}
          <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Enter an amount.' }, { type: 'number', min: 0.01, message: 'Amount must be greater than zero.' }]}>
            <InputNumber className="w-full" min={0} controls={false} prefix="₹" />
          </Form.Item>
          <Form.Item name="mode" label="Mode" rules={[{ required: true }]}>
            <Select options={RECEIPT_MODES.map((m) => ({ value: m, label: modeLabel(m) }))} />
          </Form.Item>
          <Form.Item name="ledgerAccountId" label="Deposit into (Cash/Bank)" rules={[{ required: true, message: 'Pick the deposit account.' }]}>
            <Select showSearch optionFilterProp="label" placeholder="Cash / Bank ledger" options={ledgerOptions} />
          </Form.Item>
          <Form.Item name="receiptDate" label="Receipt date" rules={[{ required: true }]}>
            <DatePicker className="w-full" format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="bankReference" label="Reference (optional)">
            <Input placeholder="UPI / NEFT / cheque reference" maxLength={100} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks (optional)">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Cancel a posted receipt (reverses the ledger). */}
      <Modal
        title="Cancel receipt"
        className="erp-accounts-modal"
        open={cancelState.open}
        onCancel={() => setCancelState({ open: false, row: null, reason: '', busy: false })}
        onOk={submitCancel}
        okText="Cancel receipt"
        okButtonProps={{ danger: true, disabled: !cancelState.reason.trim() }}
        confirmLoading={cancelState.busy}
        destroyOnHidden
      >
        <div className="text-xs text-slate-500 mb-2">
          {cancelState.row ? `${cancelState.row.paymentNo} · ${formatMoney(cancelState.row.amount)}` : ''} — the posted ledger entry will be reversed.
        </div>
        <Input.TextArea
          autoFocus rows={3} maxLength={300} showCount
          placeholder="Reason for cancellation (required)"
          value={cancelState.reason}
          onChange={(e) => setCancelState((s) => ({ ...s, reason: e.target.value }))}
        />
      </Modal>

      {/* Allocate an on-account advance across confirmed proformas. */}
      <AllocateAdvanceModal
        open={allocState.open}
        receipt={allocState.receipt}
        proformas={allocState.proformas}
        saving={allocState.saving}
        onCancel={() => setAllocState({ open: false, row: null, receipt: null, proformas: [], saving: false })}
        onSave={saveAllocate}
      />
    </ErpMasterShell>
  );
}
